// pages/AccountAssetDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Building2,
  User,
  MoreVertical,
  FileText,
  Paperclip,
  Activity,
  Save,
  X as CloseIcon,
  Loader,
  Package,
  Calendar,
  DollarSign,
  Hash,
  ShoppingCart,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import apiService from "../../services/api";
import EditAccountAssetModal from "../../components/EditAccountAssetModal";
import { useTheme } from "../../contexts/ThemeContext";

const AccountAssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Notes & Attachments (for future implementation)
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  useEffect(() => {
    fetchAsset();
  }, [id]);

  const fetchAsset = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAsset(id);
      setAsset(response.data);
    } catch (error) {
      console.error("Error fetching asset:", error);
      navigate("/assets");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this asset?")) {
      try {
        await apiService.deleteAsset(id);
        navigate("/assets");
      } catch (error) {
        console.error("Error deleting asset:", error);
        alert("Failed to delete asset");
      }
    }
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);
      await apiService.updateAsset(id, { [field]: editingValue });
      await fetchAsset();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Requested: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      Ordered: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      "Original Sale": "text-green-400 bg-green-400/10 border-green-400/20",
      "Add-on": "text-purple-400 bg-purple-400/10 border-purple-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Requested":
        return <Clock size={14} />;
      case "Ordered":
        return <ShoppingCart size={14} />;
      case "Original Sale":
        return <CheckCircle size={14} />;
      case "Add-on":
        return <Package size={14} />;
      default:
        return <AlertCircle size={14} />;
    }
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none resize-none`}
                  rows={3}
                  autoFocus
                />
              ) : type === "date" ? (
                <input
                  type="date"
                  value={editingValue ? editingValue.split("T")[0] : ""}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              ) : type === "number" ? (
                <input
                  type="number"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              ) : (
                <input
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 ${isDarkMode ? "bg-gray-800 border-blue-500 text-white" : "bg-gray-100 border-blue-500 text-gray-900"} border rounded-lg focus:outline-none`}
                  autoFocus
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
              >
                <CloseIcon
                  size={16}
                  className={isDarkMode ? "text-white" : "text-gray-900"}
                />
              </button>
            </div>
          ) : (
            <>
              <p
                className={`${isDarkMode ? "text-white" : "text-gray-900"} flex-1`}
              >
                {type === "date" && value
                  ? new Date(value).toLocaleDateString()
                  : type === "number" && field === "price" && value
                    ? `£${parseFloat(value).toLocaleString()}`
                    : value || "—"}
              </p>
              <button
                onClick={() => startEditing(field, value)}
                className={`opacity-0 group-hover:opacity-100 p-1 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded transition-all`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <Loader
          className={isDarkMode ? "text-white" : "text-gray-900"}
          size={32}
        />
      </div>
    );
  }

  if (!asset) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Asset not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0A0B0F] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`border-b ${isDarkMode ? "border-gray-700 bg-[#13161B]" : "border-gray-200 bg-white"}`}
      >
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} transition-colors`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
              >
                <Edit size={16} />
                <span>Edit</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"} rounded-lg transition-colors`}
                >
                  <MoreVertical
                    size={20}
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} border rounded-lg shadow-xl z-50`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-700"} flex items-center gap-2`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} my-1`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} flex items-center gap-2 text-red-400`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Asset Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white">
                  <Package size={24} />
                </div>
                <div>
                  <h1
                    className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {asset.assetName}
                  </h1>
                  {asset.serialNumber && (
                    <div
                      className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} text-sm flex items-center gap-1`}
                    >
                      <Hash size={14} />
                      {asset.serialNumber}
                    </div>
                  )}
                </div>

                {asset.status && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium flex items-center gap-1.5 ${getStatusColor(asset.status)}`}
                  >
                    {getStatusIcon(asset.status)}
                    {asset.status}
                  </span>
                )}

                {asset.quantity > 1 && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${isDarkMode ? "bg-gray-500/10 text-gray-400 border-gray-500/20" : "bg-gray-100 text-gray-600 border-gray-300"}`}
                  >
                    Qty: {asset.quantity}
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {asset.account && (
                  <button
                    onClick={() => navigate(`/accounts/${asset.account.id}`)}
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <Building2 size={16} />
                    <span>{asset.account.name}</span>
                  </button>
                )}
                {asset.product && (
                  <button
                    onClick={() => navigate(`/products/${asset.product.id}`)}
                    className={`flex items-center gap-2 ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"} transition-colors`}
                  >
                    <Package size={16} />
                    <span>{asset.product.productName}</span>
                  </button>
                )}
                {asset.price && (
                  <div className="flex items-center gap-2">
                    <DollarSign size={16} />
                    <span>£{parseFloat(asset.price).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {asset.owner && (
              <div
                className={`flex items-center gap-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"} rounded-lg p-3`}
              >
                <div
                  className={`w-10 h-10 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-300"} flex items-center justify-center`}
                >
                  {asset.owner.profileImageUrl ? (
                    <img
                      src={asset.owner.profileImageUrl}
                      alt={asset.owner.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                    >
                      {asset.owner.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Asset Owner
                  </div>
                  <div
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {asset.owner.name}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border`}
            >
              <div
                className={`flex border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: FileText },
                  { id: "activity", label: "Activity", icon: Activity },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-6 py-3 font-medium transition-colors ${
                        activeTab === tab.id
                          ? isDarkMode
                            ? "text-blue-400 border-b-2 border-blue-400"
                            : "text-blue-600 border-b-2 border-blue-600"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {/* Basic Information */}
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Package size={18} />
                        Basic Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <EditableField
                          label="Asset Name"
                          field="assetName"
                          value={asset.assetName}
                        />
                        <EditableField
                          label="Serial Number"
                          field="serialNumber"
                          value={asset.serialNumber}
                        />
                        <EditableField
                          label="Status"
                          field="status"
                          value={asset.status}
                          options={[
                            { value: "Requested", label: "Requested" },
                            { value: "Ordered", label: "Ordered" },
                            { value: "Original Sale", label: "Original Sale" },
                            { value: "Add-on", label: "Add-on" },
                          ]}
                        />
                        <EditableField
                          label="Quantity"
                          field="quantity"
                          value={asset.quantity}
                          type="number"
                        />
                        <div className="col-span-2">
                          <EditableField
                            label="Price"
                            field="price"
                            value={asset.price}
                            type="number"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        <Calendar size={18} />
                        Important Dates
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <EditableField
                          label="Purchase Date"
                          field="purchaseDate"
                          value={asset.purchaseDate}
                          type="date"
                        />
                        <EditableField
                          label="Install Date"
                          field="installDate"
                          value={asset.installDate}
                          type="date"
                        />
                        <div className="col-span-2">
                          <EditableField
                            label="Usage End Date"
                            field="usageEndDate"
                            value={asset.usageEndDate}
                            type="date"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description & Notes */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Description
                      </h3>
                      <EditableField
                        label=""
                        field="description"
                        value={asset.description}
                        type="textarea"
                      />
                    </div>

                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Order Notes
                      </h3>
                      <EditableField
                        label=""
                        field="orderNotes"
                        value={asset.orderNotes}
                        type="textarea"
                      />
                    </div>

                    {/* System Information */}
                    <div
                      className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} pt-6`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created By
                          </label>
                          <p
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} flex items-center gap-2`}
                          >
                            <User size={16} />
                            {asset.creator?.name || "System"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(asset.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified By
                          </label>
                          <p
                            className={`${isDarkMode ? "text-white" : "text-gray-900"} flex items-center gap-2`}
                          >
                            <User size={16} />
                            {asset.lastModifiedBy?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Last Modified Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(asset.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === "activity" && (
                  <div
                    className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Activity size={48} className="mx-auto mb-2 opacity-50" />
                    <p>Activity tracking coming soon</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Quick Information
              </h3>
              <div className="space-y-3">
                {asset.account && (
                  <button
                    onClick={() => navigate(`/accounts/${asset.account.id}`)}
                    className={`w-full p-3 ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"} rounded-lg transition-colors text-left`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Building2 size={14} />
                      Account
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {asset.account.name}
                    </div>
                  </button>
                )}

                {asset.product && (
                  <button
                    onClick={() => navigate(`/products/${asset.product.id}`)}
                    className={`w-full p-3 ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"} rounded-lg transition-colors text-left`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Package size={14} />
                      Product
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {asset.product.productName}
                    </div>
                  </button>
                )}

                {asset.serialNumber && (
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Hash size={14} />
                      Serial Number
                    </div>
                    <div
                      className={`font-medium font-mono text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {asset.serialNumber}
                    </div>
                  </div>
                )}

                {asset.quantity && (
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <Package size={14} />
                      Quantity
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {asset.quantity}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Info Card */}
            {asset.price && (
              <div
                className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Financial Information
                </h3>
                <div className="space-y-3">
                  <div
                    className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-lg`}
                  >
                    <div
                      className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                    >
                      <DollarSign size={14} />
                      Unit Price
                    </div>
                    <div
                      className={`font-medium text-xl ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                    >
                      £{parseFloat(asset.price).toLocaleString()}
                    </div>
                  </div>

                  {asset.quantity > 1 && (
                    <div
                      className={`p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-lg`}
                    >
                      <div
                        className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1`}
                      >
                        <DollarSign size={14} />
                        Total Value
                      </div>
                      <div
                        className={`font-medium text-xl ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                      >
                        £
                        {(
                          parseFloat(asset.price) * asset.quantity
                        ).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timeline Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Timeline
              </h3>
              <div className="space-y-3">
                {asset.purchaseDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Purchased
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(asset.purchaseDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {asset.installDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Installed
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(asset.installDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {asset.usageEndDate && (
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Usage End Date
                    </span>
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {new Date(asset.usageEndDate).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {!asset.purchaseDate &&
                  !asset.installDate &&
                  !asset.usageEndDate && (
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} text-center py-4`}
                    >
                      No dates recorded
                    </p>
                  )}
              </div>
            </div>

            {/* Status Card */}
            <div
              className={`${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-lg border p-4`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Status
              </h3>
              <div
                className={`flex items-center gap-3 p-4 rounded-lg border ${getStatusColor(asset.status)}`}
              >
                {getStatusIcon(asset.status)}
                <div className="flex-1">
                  <div className="font-medium">{asset.status}</div>
                  <div className="text-xs opacity-75">Current Status</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditAccountAssetModal
          asset={asset}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchAsset();
          }}
        />
      )}
    </div>
  );
};

export default AccountAssetDetail;
